// GtSettings.js — Settings (C1-USAGE, 3 Nov 2026). First section: where the AI and search spend goes, by feature.
// Read from llm_calls through /api/usage. Exa does not report usage, so its cost is an estimate; call counts are exact.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthFetch } from './GtLexaAdmin';
import gtConfirm from '../../components/GtConfirm';
import GtEditor from '../../components/GtEditor';

const PROVIDERS = [['exa', 'Exa search'], ['anthropic', 'Claude'], ['openrouter', 'Other models']];
const money = (v) => '$' + (Number(v) || 0).toFixed(Number(v) >= 100 ? 0 : 2);
const num = (v) => (Number(v) || 0).toLocaleString('en-GB');
function ago(v) {
  if (!v) return '';
  const m = Math.round((Date.now() - new Date(v).getTime()) / 60000);
  if (m < 60) return m <= 1 ? 'just now' : m + ' min ago';
  const h = Math.round(m / 60);
  if (h < 48) return h + ' h ago';
  return Math.round(h / 24) + ' days ago';
}

// Each section folds away to one line with its summary. Which ones are open is remembered on this browser.
const OPEN_KEY = 'gt.settings.open';
function readOpen() { try { return JSON.parse(window.localStorage.getItem(OPEN_KEY) || '{}') || {}; } catch (e) { return {}; } }
function Section({ id, title, summary, tone, children }) {
  const [open, setOpen] = useState(() => !!readOpen()[id]);
  function toggle() {
    const next = !open;
    setOpen(next);
    try { const all = readOpen(); all[id] = next; window.localStorage.setItem(OPEN_KEY, JSON.stringify(all)); } catch (e) { /* fine */ }
  }
  return (
    <section className={'gtst-card' + (open ? ' gtst-card--open' : '')}>
      <button type="button" className="gtst-toggle" aria-expanded={open} onClick={toggle}>
        <span className="gtst-tt"><b>{title}</b>{summary && <em className={tone ? 'gtst-tone-' + tone : ''}>{summary}</em>}</span>
        <svg className="gtst-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && <div className="gtst-body">{children}</div>}
    </section>
  );
}

function Usage({ authFetch }) {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [prov, setProv] = useState('exa');
  useEffect(() => { setD(null); setErr(''); authFetch('/api/usage?days=' + days).then(setD).catch((e) => setErr(e.message)); }, [authFetch, days]);
  const totals = useMemo(() => {
    const t = {};
    (d ? d.rows : []).forEach((r) => { t[r.provider] = t[r.provider] || { calls: 0, cost: 0, week: 0 }; t[r.provider].calls += r.calls; t[r.provider].cost += r.cost_usd; t[r.provider].week += r.calls_7d; });
    return t;
  }, [d]);
  const rows = d ? d.rows.filter((r) => r.provider === prov) : [];
  const max = rows.reduce((m, r) => Math.max(m, r.calls), 1);
  const peak = d ? d.exa_daily.reduce((m, x) => Math.max(m, x.calls), 1) : 1;
  return (
    <Section id="usage" title="Usage by feature" summary={d ? (totals.exa ? totals.exa.calls : 0).toLocaleString('en-GB') + ' Exa searches and ' + ((totals.anthropic ? totals.anthropic.calls : 0) + (totals.openrouter ? totals.openrouter.calls : 0)).toLocaleString('en-GB') + ' AI calls in the last ' + days + ' days' : 'Loading…'}>
      <div className="gtst-h">
        <div><span>Every search and AI call the platform makes, grouped by what made it. Counts are exact. Exa does not report its costs, so its figures are estimates.</span></div>
        <div className="gtst-days" role="tablist" aria-label="Period">
          {[7, 30, 90].map((n) => <button key={n} type="button" role="tab" aria-selected={days === n} className={days === n ? 'on' : ''} onClick={() => setDays(n)}>{n} days</button>)}
        </div>
      </div>
      {err && <p className="gtiv-err">{err}</p>}
      {!d ? (err ? null : <p className="gtst-muted">Loading…</p>) : (
        <>
          <div className="gtst-tiles">
            {PROVIDERS.map(([k, l]) => {
              const t = totals[k] || { calls: 0, cost: 0, week: 0 };
              return (
                <button key={k} type="button" className={'gtst-tile' + (prov === k ? ' gtst-tile--on' : '')} onClick={() => setProv(k)}>
                  <em>{l}</em><b>{num(t.calls)}</b><span>calls · {money(t.cost)}{k === 'exa' ? ' est.' : ''} · {num(t.week)} in the last 7 days</span>
                </button>
              );
            })}
          </div>
          {prov === 'exa' && d.exa_daily.length > 0 && (
            <div className="gtst-spark" aria-label="Exa calls per day">
              {d.exa_daily.map((x) => <i key={x.day} title={x.day + ': ' + num(x.calls) + ' calls'} style={{ height: Math.max(3, Math.round((x.calls / peak) * 56)) + 'px' }} />)}
            </div>
          )}
          {rows.length === 0 ? <p className="gtst-muted">No calls in this period.</p> : (
            <ul className="gtst-list">
              {rows.map((r) => (
                <li key={r.task}>
                  <div className="gtst-l"><b>{r.label}</b><em>{r.task}{r.failed ? ' · ' + num(r.failed) + ' failed' : ''} · last {ago(r.last_at)}</em></div>
                  <div className="gtst-bar"><i style={{ width: Math.max(2, Math.round((r.calls / max) * 100)) + '%' }} /></div>
                  <div className="gtst-n"><b>{num(r.calls)}</b><em>{money(r.cost_usd)}</em></div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Section>
  );
}

// C1-SPEND: daily Exa allowances per feature, an on/off switch each, and a monthly budget.
function SpendLimits({ authFetch }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState({ monthly: '', groups: {} });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  function take(x) { setD(x); setEdit({ monthly: String(x.monthly_usd), groups: Object.fromEntries(x.groups.map((g) => [g.key, { daily: String(g.daily), on: g.on }])) }); }
  useEffect(() => { authFetch('/api/settings/spend').then(take).catch((e) => setMsg(e.message)); }, [authFetch]);
  if (!d) return <Section id="spend" title="Spend limits" summary={msg || 'Loading…'} tone={msg ? 'rose' : null}><p className="gtiv-err">{msg}</p></Section>;
  const changed = String(d.monthly_usd) !== edit.monthly.trim() || d.groups.some((g) => String(g.daily) !== String(edit.groups[g.key].daily).trim() || g.on !== edit.groups[g.key].on);
  async function save() {
    const isNum = (v) => String(v).trim() !== '' && Number.isFinite(Number(String(v).trim())) && Number(String(v).trim()) >= 0;
    if (!isNum(edit.monthly)) { setMsg('Enter a monthly budget in dollars, or 0 for no budget.'); return; }
    const bad = d.groups.find((g) => !isNum(edit.groups[g.key].daily));
    if (bad) { setMsg('Enter a daily limit for ' + bad.label + ', or 0 to pause it.'); return; }
    setBusy(true); setMsg('');
    const groups = {};
    d.groups.forEach((g) => { const e = edit.groups[g.key]; groups[g.key] = { daily: Number(String(e.daily).trim()), on: e.on }; });
    try { take(await authFetch('/api/settings/spend', { method: 'PUT', body: JSON.stringify({ monthly_usd: Number(edit.monthly.trim()), groups }) })); setMsg('Saved.'); } catch (e) { setMsg(e.message); }
    setBusy(false);
  }
  const pct = d.monthly_usd > 0 ? Math.min(100, Math.round((d.month_usd / d.monthly_usd) * 100)) : 0;
  const paused = d.groups.filter((g) => g.on && g.today >= g.daily);
  const off = d.groups.filter((g) => !g.on);
  const setG = (k, patch) => setEdit((x) => ({ ...x, groups: { ...x.groups, [k]: { ...x.groups[k], ...patch } } }));
  return (
    <Section id="spend" title="Spend limits" tone={paused.length || pct >= 100 ? 'rose' : pct >= 80 ? 'amber' : null}
      summary={'$' + d.month_usd.toFixed(2) + (d.monthly_usd > 0 ? ' of $' + d.monthly_usd + ' this month' : ' this month, no budget') + (paused.length ? ' · ' + paused.length + ' paused today' : '') + (off.length ? ' · ' + off.length + ' switched off' : '')}>
      <div className="gtst-h">
        <div><span>Every Exa search and every Apollo credit is checked against these first. A feature that reaches its daily limit pauses until midnight; when the monthly Exa budget is used up, all Exa searches pause. Either way you get an amber alert. Switch a feature off to stop its searches altogether.</span></div>
      </div>
      <div className="gtst-budget">
        <label><span>Monthly Exa budget</span><div className="gtst-money"><i>$</i><input className="gtiv-in" inputMode="decimal" value={edit.monthly} onChange={(e) => setEdit((x) => ({ ...x, monthly: e.target.value }))} aria-label="Monthly Exa budget in US dollars" /></div></label>
        <div className="gtst-meter">
          <div className="gtst-meter-t"><b>${d.month_usd.toFixed(2)}</b><span>estimated so far this month{d.monthly_usd > 0 ? ', ' + pct + '% of the budget' : '. No budget set: 0 means no limit'}</span></div>
          <div className={'gtst-bar gtst-bar--big' + (pct >= 100 ? ' gtst-bar--rose' : pct >= 80 ? ' gtst-bar--amber' : '')}><i style={{ width: Math.max(1, pct) + '%' }} /></div>
        </div>
      </div>
      <ul className="gtst-list gtst-limits">
        {d.groups.map((g) => {
          const e = edit.groups[g.key];
          const lim = Number(e.daily) || 0;
          const p = lim ? Math.min(100, Math.round((g.today / lim) * 100)) : 100;
          return (
            <li key={g.key} className={e.on ? '' : 'gtst-off'}>
              <div className="gtst-l"><b>{g.label}</b><em>{g.today.toLocaleString('en-GB')} used today{g.today >= lim && e.on ? ', paused until midnight' : ''}</em></div>
              <div className={'gtst-bar' + (p >= 100 ? ' gtst-bar--rose' : p >= 80 ? ' gtst-bar--amber' : '')}><i style={{ width: Math.max(1, p) + '%' }} /></div>
              <label className="gtst-lim"><input className="gtiv-in" inputMode="numeric" value={e.daily} onChange={(ev) => setG(g.key, { daily: ev.target.value })} aria-label={g.label + ' daily limit'} /><span>a day</span></label>
              <button type="button" role="switch" aria-checked={e.on} aria-label={g.label + (e.on ? ' is on' : ' is off')} className={'gtfm-sw' + (e.on ? ' gtfm-sw--on' : '')} onClick={() => setG(g.key, { on: !e.on })}><i /></button>
            </li>
          );
        })}
      </ul>
      <div className="gtst-foot">
        {msg && <span className={msg === 'Saved.' ? 'gtst-ok' : 'gtiv-err'}>{msg}</span>}
        <button type="button" className="gt-btn" disabled={!changed || busy} onClick={() => take(d)}>Undo</button>
        <button type="button" className="gt-btn gt-btn--primary" disabled={!changed || busy} onClick={save}>{busy ? 'Saving…' : 'Save limits'}</button>
      </div>
    </Section>
  );
}

// C1-CONNECT: is each outside service working. Read-only; keys never leave Railway.
const DOT = { ok: 'Working', warn: 'Needs a look', error: 'Not working', off: 'Not set up' };
function Connections({ authFetch }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setBusy(true); setErr(''); return authFetch('/api/settings/connections').then(setD).catch((e) => setErr(e.message)).finally(() => setBusy(false)); }, [authFetch]);
  useEffect(() => { load(); }, [load]);
  const list = d ? d.services : [];
  const bad = list.filter((x) => x.status === 'error');
  const look = list.filter((x) => x.status === 'warn');
  const okN = list.filter((x) => x.status === 'ok').length;
  const summary = !d ? (err || 'Checking…') : bad.length ? bad.map((x) => x.name + ': ' + x.note).join(' · ') : okN + ' working' + (look.length ? ' · ' + look.length + ' to look at' : '');
  return (
    <Section id="connections" title="Connections" summary={summary} tone={err || bad.length ? 'rose' : look.length ? 'amber' : null}>
      <div className="gtst-h">
        <div><span>Every outside service the platform relies on, and whether it is working. Keys are kept in Railway and never shown here.</span></div>
        <button type="button" className="gt-btn" disabled={busy} onClick={load}>{busy ? 'Checking…' : 'Check again'}</button>
      </div>
      {err && <p className="gtiv-err">{err}</p>}
      <ul className="gtst-conn">
        {list.map((x) => (
          <li key={x.key} className={'gtst-c gtst-c--' + x.status}>
            <div className="gtst-c-h"><b>{x.name}</b><span className="gtst-pill"><i />{DOT[x.status]}</span></div>
            <p className="gtst-c-w">{x.what}</p>
            <p className="gtst-c-n">{x.note}{x.at ? ' · ' + ago(x.at) : ''}</p>
            {x.manage && (/^https?:/.test(x.manage) ? <a href={x.manage} target="_blank" rel="noopener noreferrer">Open {x.name} &#8599;</a> : <a href={x.manage}>Open</a>)}
          </li>
        ))}
      </ul>
    </Section>
  );
}

// C1-AUTO: everything that runs by itself, with a switch each.
const RUN_WORD = { done: 'finished', published: 'live', scheduled: 'booked to go live', draft: 'ready to read', writing: 'being written', running: 'running now', failed: 'failed', posted: 'posted', sent: 'sent', needs_you: 'needs you', held: 'held' };
function whenDone(v) { try { return new Date(v).toLocaleDateString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { return ''; } }
function Automations({ authFetch, navigate }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => { authFetch('/api/settings/automations').then(setD).catch((e) => setErr(e.message)); }, [authFetch]);
  async function flip(x) {
    setBusy(x.key); setErr('');
    try { setD(await authFetch('/api/settings/automations/' + x.key, { method: 'PUT', body: JSON.stringify({ on: !x.on }) })); } catch (e) { setErr(e.message); }
    setBusy('');
  }
  const items = d ? d.items : [];
  const off = items.filter((x) => !x.on);
  const failed = items.filter((x) => x.on && x.last && x.last.status === 'failed');
  const summary = !d ? (err || 'Loading…') : !d.live ? 'Not production: nothing here runs by itself' : failed.length ? failed.map((x) => x.name + ' failed last time').join(' · ') : off.length ? off.length + ' switched off: ' + off.map((x) => x.name).join(', ') : 'All ' + items.length + ' on';
  return (
    <Section id="automations" title="Automations" summary={summary} tone={err || failed.length ? 'rose' : off.length || (d && !d.live) ? 'amber' : null}>
      <div className="gtst-h"><div><span>Everything the platform does on a schedule. Switch one off and it stops until you switch it back on; anything already written stays where it is.{d && !d.live ? ' This copy is not production, so none of these run by themselves here.' : ''}</span></div></div>
      {err && <p className="gtiv-err">{err}</p>}
      <ul className="gtst-list gtst-auto">
        {items.map((x) => (
          <li key={x.key} className={x.on ? '' : 'gtst-off'}>
            <div className="gtst-l"><b>{x.name}</b><em>{x.when}</em></div>
            <div className={'gtst-last' + (x.last && x.last.status === 'failed' ? ' gtst-last--bad' : '')}>
              {x.last && x.last.at ? <>Last: {whenDone(x.last.at)}{x.last.status ? ', ' + (RUN_WORD[x.last.status] || String(x.last.status).replace(/_/g, ' ')) : ''}{x.last.error ? ' (' + String(x.last.error).slice(0, 90) + ')' : ''}</> : 'Not run yet'}
            </div>
            <button type="button" className="gtiv-link" onClick={() => navigate(x.link)}>Open</button>
            <button type="button" role="switch" aria-checked={x.on} aria-label={x.name + (x.on ? ' is on' : ' is off')} disabled={busy === x.key} className={'gtfm-sw' + (x.on ? ' gtfm-sw--on' : '')} onClick={() => flip(x)}><i /></button>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// C1-SENDING: every address the platform sends from, and what each one is for.
const USES = [['candidates', 'Candidate email'], ['client_outreach', 'Client outreach']];
const DOMAIN = { verified: 'Domain verified', not_added: 'Domain not in Resend', unknown: 'Domain status unknown' };
function domainWord(st) { return DOMAIN[st] || 'Domain ' + String(st || '').replace(/_/g, ' '); }
function Sending({ authFetch }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState(null); // { id?, email, display_name, forward_to, purposes }
  const load = useCallback(() => authFetch('/api/settings/sending').then(setD).catch((e) => setErr(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  async function setUses(sn, key) {
    const next = sn.purposes.includes(key) ? sn.purposes.filter((p) => p !== key) : sn.purposes.concat([key]);
    if (!next.length) { setErr('An address needs at least one use.'); return; }
    setBusy('u' + sn.id); setErr('');
    try { await authFetch('/api/settings/sending/senders/' + sn.id + '/purposes', { method: 'PUT', body: JSON.stringify({ purposes: next }) }); await load(); } catch (e) { setErr(e.message); }
    setBusy('');
  }
  async function makeDefault(sn) {
    setBusy('d' + sn.id); setErr('');
    try { await authFetch('/api/email/senders/' + sn.id, { method: 'PATCH', body: JSON.stringify({ is_default: true }) }); await load(); } catch (e) { setErr(e.message); }
    setBusy('');
  }
  async function remove(sn) {
    if (!(await gtConfirm({ title: 'Remove ' + sn.email + '?', message: 'Nothing already sent is changed. You can add it again later.', confirmLabel: 'Remove', tone: 'danger' }))) return;
    setBusy('r' + sn.id); setErr('');
    try { await authFetch('/api/email/senders/' + sn.id, { method: 'DELETE' }); await load(); } catch (e) { setErr(e.message); }
    setBusy('');
  }
  async function save() {
    const f = form;
    if (!f.purposes.length) { setErr('Choose at least one use.'); return; }
    setBusy('save'); setErr('');
    try {
      const body = { display_name: f.display_name, forward_to: f.forward_to || '', signature_html: f.signature_html || '' };
      if (f.purposes.includes('client_outreach')) body.reply_to = f.reply_to || ''; // C1-SEND: replies also go to this inbox
      let id = f.id;
      if (id) await authFetch('/api/email/senders/' + id, { method: 'PATCH', body: JSON.stringify(body) });
      else id = (await authFetch('/api/email/senders', { method: 'POST', body: JSON.stringify({ ...body, email: f.email, capture_replies: true }) })).sender.id;
      try { await authFetch('/api/settings/sending/senders/' + id + '/purposes', { method: 'PUT', body: JSON.stringify({ purposes: f.purposes }) }); }
      catch (e) { if (!f.id) await authFetch('/api/email/senders/' + id, { method: 'DELETE' }).catch(() => null); throw e; } // a new address is never left with the wrong use
      setForm(null); await load();
    } catch (e) { setErr(e.message); }
    setBusy('');
  }
  const senders = d ? d.senders : [];
  const outreach = senders.filter((x) => x.purposes.includes('client_outreach'));
  const unverified = senders.concat(d ? d.system : []).filter((x) => x.domain_status !== 'verified' && x.domain_status !== 'unknown');
  const summary = !d ? (err || 'Loading…') : senders.length + (senders.length === 1 ? ' address' : ' addresses') + ' · ' + outreach.length + ' for client outreach' + (unverified.length ? ' · ' + unverified.length + ' domain' + (unverified.length === 1 ? '' : 's') + ' not verified' : ' · all domains verified');
  return (
    <Section id="sending" title="Sending" summary={summary} tone={err ? 'rose' : unverified.length ? 'amber' : null}>
      <div className="gtst-h">
        <div><span>The addresses email goes out from. Each one says what it is for: candidate email and sequences, client outreach, or both. Client outreach can use several addresses. Replies to every address come back into the platform.</span></div>
        {!form && <button type="button" className="gt-btn gt-btn--primary" onClick={() => setForm({ email: '', display_name: '', forward_to: '', reply_to: '', purposes: ['client_outreach'], signature_html: '' })}>Add an address</button>}
      </div>
      <p className="gtst-mailnote">These addresses send through Resend: candidate email, sign-in links and shortlist links. Auto outreach, the Insights email and candidate messaging go from GTCO&rsquo;s own mailboxes, in <Link to="/sending">Sending</Link>.</p>
      {err && <p className="gtiv-err">{err}</p>}
      {form && (
        <div className="gtst-form">
          <div className="gtst-form-g">
            {!form.id && <label><span>Email address</span><input className="gtiv-in" value={form.email} placeholder="jb@t.generativetalent.app" onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>}
            <label><span>Name people see</span><input className="gtiv-in" value={form.display_name} placeholder="Jimmy Braimah" onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></label>
            <label><span>Copy every reply to (optional)</span><input className="gtiv-in" value={form.forward_to} placeholder="you@gtco.net" onChange={(e) => setForm({ ...form, forward_to: e.target.value })} /></label>
            {form.purposes.includes('client_outreach') && <label><span>Client outreach replies also go to</span><input className="gtiv-in" value={form.reply_to || ''} placeholder="jb@generativetalent.co" onChange={(e) => setForm({ ...form, reply_to: e.target.value })} /></label>}
          </div>
          <div className="gtst-sig">
            <span>Email signature</span>
            <GtEditor value={form.signature_html || ''} minHeight={200} onChange={(v) => setForm((x) => ({ ...x, signature_html: v }))} />
            <em>Goes under every email from this address, candidate email and client outreach alike. Left empty, client outreach signs with the name above, Generative Talent Co and both phone numbers.</em>
          </div>
          <div className="gtst-uses"><span>Used for</span>{USES.map(([k, l]) => (
            <button key={k} type="button" className={'gtst-use' + (form.purposes.includes(k) ? ' gtst-use--on' : '')} onClick={() => setForm({ ...form, purposes: form.purposes.includes(k) ? form.purposes.filter((p) => p !== k) : form.purposes.concat([k]) })}>{l}</button>
          ))}</div>
          <div className="gtst-foot">
            <button type="button" className="gt-btn" disabled={busy === 'save'} onClick={() => setForm(null)}>Cancel</button>
            <button type="button" className="gt-btn gt-btn--primary" disabled={busy === 'save'} onClick={save}>{busy === 'save' ? 'Saving…' : form.id ? 'Save' : 'Add address'}</button>
          </div>
        </div>
      )}
      <ul className="gtst-send">
        {senders.map((sn) => (
          <li key={sn.id}>
            <div className="gtst-send-t">
              <b>{sn.display_name}</b><em>{sn.email}</em>
              <span className={'gtst-dom' + (sn.domain_status === 'verified' ? ' gtst-dom--ok' : '')}>{domainWord(sn.domain_status)}{sn.domain_status === 'verified' && sn.receiving && sn.receiving !== 'enabled' ? ', receiving off' : ''}</span>
              {sn.is_default && <span className="gtst-dom gtst-dom--ok">Default for candidate email</span>}
              <span className={'gtst-dom' + (sn.has_signature ? ' gtst-dom--ok' : '')}>{sn.has_signature ? 'Signature set' : 'No signature'}</span>
            </div>
            <div className="gtst-uses">{USES.map(([k, l]) => (
              <button key={k} type="button" disabled={busy === 'u' + sn.id} className={'gtst-use' + (sn.purposes.includes(k) ? ' gtst-use--on' : '')} onClick={() => setUses(sn, k)}>{l}</button>
            ))}</div>
            <p className="gtst-send-n">{sn.purposes.includes('client_outreach') ? 'Replies go to ' + (sn.reply_to || 'jb@generativetalent.co') + ' and come back into the platform' : sn.capture_replies ? 'Replies come back into the platform' : 'Replies go to ' + (sn.reply_to || 'the address itself')}{sn.forward_to ? ', with a copy to ' + sn.forward_to : ''}.</p>
            <div className="gtst-send-a">
              {!sn.is_default && sn.purposes.includes('candidates') && <button type="button" className="gtiv-link" disabled={busy === 'd' + sn.id} onClick={() => makeDefault(sn)}>Make default</button>}
              <button type="button" className="gtiv-link" onClick={() => setForm({ id: sn.id, display_name: sn.display_name, forward_to: sn.forward_to || '', reply_to: sn.reply_to || '', purposes: sn.purposes, signature_html: sn.signature_html || '' })}>Edit</button>
              <button type="button" className="gtiv-link gtst-rm" disabled={busy === 'r' + sn.id} onClick={() => remove(sn)}>Remove</button>
            </div>
          </li>
        ))}
        {d && !senders.length && <li className="gtst-muted">No addresses yet.</li>}
      </ul>
      {d && (
        <div className="gtst-sys">
          <b>Set in Railway</b>
          {d.system.map((x) => (
            <div key={x.key} className="gtst-sys-r"><span>{x.name}</span><em>{x.address}</em><span className={'gtst-dom' + (x.domain_status === 'verified' ? ' gtst-dom--ok' : '')}>{domainWord(x.domain_status)}</span><i>{x.env}</i></div>
          ))}
          <div className="gtst-sys-r"><span>Beth's booking link</span><em><a href={d.booking_url} target="_blank" rel="noopener noreferrer">{d.booking_url}</a></em><span /><i>BETH_BOOKING_URL</i></div>
        </div>
      )}
    </Section>
  );
}

export default function GtSettings({ getToken }) {
  const authFetch = useAuthFetch(getToken);
  const navigate = useNavigate();
  return (
    <div className="gtc-wrap gtst">
      <div className="gtc-head"><div><h1 className="gtc-title">Settings</h1><p className="gtc-sub">How the platform is running, and what it costs.</p></div></div>
      <Connections authFetch={authFetch} />
      <Automations authFetch={authFetch} navigate={navigate} />
      <Sending authFetch={authFetch} />
      <SpendLimits authFetch={authFetch} />
      <Usage authFetch={authFetch} />
    </div>
  );
}
